import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Clock, Lock, Mail, ShieldCheck } from "lucide-react";
import Header from "./Header";
import Footer from "./Footer";
import BackToTop from "./BackToTop";
import { Reveal } from "./Reveal";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useAuth } from "../context/AuthContext";

/**
 * Combined Login / Register page. Tab switches between modes.
 * Used as the public entry for the Agency Portal (and any user wanting an
 * account for faster checkout in the future).
 */
export const AuthPage = ({ defaultMode = "login", defaultRole = "private" }) => {
  const [mode, setMode] = useState(defaultMode); // "login" | "register"
  const [role, setRole] = useState(defaultRole); // "private" | "agency"
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  // When an agency signup succeeds we keep them on this page and render a
  // pending-approval screen instead of routing into the portal.
  const [pendingApproval, setPendingApproval] = useState(null);
  const [form, setForm] = useState({
    email: "",
    password: "",
    name: "",
    organization: "",
  });
  const update = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();

  // Prefill email + auto-switch to "register" when arriving from an email
  // pay-link invite. `?email=…&mode=register` are recognised. See
  // send_manual_invoice_link → Create Agency Account CTA.
  useEffect(() => {
    const emailQ = params.get("email");
    const modeQ = params.get("mode");
    if (emailQ) {
      setForm((s) => ({ ...s, email: emailQ }));
    }
    if (modeQ === "register" || modeQ === "login") {
      setMode(modeQ);
    }
  }, [params]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.email || !form.password) { setError("Email and password required."); return; }
    if (mode === "register" && !form.name.trim()) { setError("Name is required."); return; }
    setSubmitting(true);
    const result = mode === "login"
      ? await login(form.email.trim().toLowerCase(), form.password)
      : await register({
          email: form.email.trim().toLowerCase(),
          password: form.password,
          name: form.name.trim(),
          role,
          organization: form.organization.trim() || null,
        });
    setSubmitting(false);
    if (result.ok) {
      if (result.pending) {
        // Agency signup awaiting admin approval. Stay on this page and
        // surface a friendly status panel.
        setPendingApproval({
          email: form.email.trim().toLowerCase(),
          name: form.name.trim(),
          organization: form.organization.trim() || null,
        });
        return;
      }
      // Admins land on the dashboard by default; everyone else goes back
      // to the page they were trying to reach (or /menu). An explicit
      // `location.state.from` always wins so deep-link flows aren't broken.
      const explicitFrom = location.state?.from;
      const isAdmin = result.user?.role === "admin";
      const target = explicitFrom || (isAdmin ? "/admin/orders" : "/menu");
      navigate(target, { replace: true });
    } else {
      setError(result.error);
    }
  };

  return (
    <div className="App font-sans bg-white text-brand-charcoal antialiased">
      <Header />
      <main>
        <section className="pt-24 sm:pt-28 lg:pt-32 pb-12 bg-brand-charcoal grain">
          <div className="max-w-5xl mx-auto px-5 sm:px-6 lg:px-10">
            <Reveal>
              <Link
                to="/"
                data-testid="auth-back-btn"
                className="inline-flex items-center gap-2 rounded-full bg-white/10 hover:bg-white/15 text-white px-3.5 py-2 text-[11px] font-bold uppercase tracking-[0.14em] transition-colors"
              >
                <ArrowLeft className="h-3.5 w-3.5" />
                Back to Home
              </Link>
            </Reveal>
            <Reveal delay={1}>
              <div className="mt-6 inline-flex items-center gap-2 rounded-full bg-brand-teal/15 border border-brand-teal/40 px-3 py-1.5 text-[10.5px] font-bold uppercase tracking-[0.16em] text-brand-teal">
                <ShieldCheck className="h-3.5 w-3.5" />
                Secure Account Access
              </div>
            </Reveal>
            <Reveal delay={2}>
              <h1 className="mt-4 font-display font-black text-4xl sm:text-5xl tracking-tight text-white leading-[1.04]">
                {mode === "login" ? "Sign in." : "Create an account."}
              </h1>
            </Reveal>
            <Reveal delay={3}>
              <p className="mt-3 max-w-xl text-[15px] text-white/70">
                {location.state?.gate === "menu"
                  ? mode === "login"
                    ? "Sign in to see our full menu and pricing."
                    : "Create a free account to see our full menu and pricing. Already have one? Sign in below."
                  : mode === "login"
                  ? "Agency users — sign in to access institutional pricing and PO checkout. Private buyers can also save their info."
                  : "Sign up as an Agency to unlock PO-based checkout and institutional pricing, or as a Private buyer for faster card checkout."}
              </p>
            </Reveal>
          </div>
        </section>

        <section className="py-12 sm:py-16 bg-brand-gray">
          <div className="max-w-md mx-auto px-5 sm:px-6">
            {pendingApproval ? (
              <div
                data-testid="auth-pending-approval"
                className="rounded-3xl bg-white border border-black/5 p-6 sm:p-8 shadow-[0_18px_50px_-30px_rgba(17,17,17,0.25)] text-center"
              >
                <div className="mx-auto inline-flex h-14 w-14 items-center justify-center rounded-full bg-brand-teal/15 text-brand-teal">
                  <Clock className="h-6 w-6" />
                </div>
                <h2 className="mt-5 font-display font-black text-2xl text-brand-charcoal tracking-tight leading-tight">
                  Application received.
                </h2>
                <p className="mt-3 text-[14px] text-brand-charcoal/70 leading-relaxed">
                  Thanks, <strong className="text-brand-charcoal">{pendingApproval.name.split(" ")[0]}</strong> — your agency
                  account{pendingApproval.organization ? ` for ${pendingApproval.organization}` : ""} is awaiting admin approval.
                  We'll email <span className="font-bold text-brand-charcoal">{pendingApproval.email}</span> the moment your
                  account is live (typically within one business day).
                </p>
                <div className="mt-5 rounded-2xl bg-brand-cream/60 border border-black/5 p-4 text-left text-[12.5px] text-brand-charcoal/75 leading-relaxed">
                  <div className="inline-flex items-center gap-2 text-[10.5px] font-bold uppercase tracking-[0.14em] text-brand-teal">
                    <Mail className="h-3.5 w-3.5" />
                    What's next?
                  </div>
                  <p className="mt-2">
                    Once you receive the approval email, sign in here with the same email and password to access institutional
                    pricing and PO-based checkout.
                  </p>
                </div>
                <div className="mt-6 grid grid-cols-2 gap-2">
                  <Link
                    to="/menu"
                    data-testid="auth-pending-browse-menu"
                    className="inline-flex items-center justify-center gap-2 rounded-full bg-white border border-brand-charcoal/15 text-brand-charcoal px-4 py-3 text-[11.5px] font-bold uppercase tracking-[0.1em] hover:bg-brand-cream transition-colors"
                  >
                    Browse the Menu
                  </Link>
                  <Link
                    to="/"
                    data-testid="auth-pending-home"
                    className="inline-flex items-center justify-center gap-2 rounded-full bg-brand-charcoal text-white px-4 py-3 text-[11.5px] font-bold uppercase tracking-[0.1em] hover:bg-brand-teal transition-colors"
                  >
                    Back to Home
                    <ArrowRight className="h-3.5 w-3.5" />
                  </Link>
                </div>
              </div>
            ) : (
            <div className="rounded-3xl bg-white border border-black/5 p-6 sm:p-8 shadow-[0_18px_50px_-30px_rgba(17,17,17,0.25)]">
              {/* Tabs */}
              <div className="grid grid-cols-2 gap-1 rounded-xl bg-brand-cream/60 p-1">
                <button
                  type="button"
                  data-testid="auth-tab-login"
                  onClick={() => { setMode("login"); setError(""); }}
                  className={`py-2 text-[11.5px] font-bold uppercase tracking-[0.14em] rounded-lg transition-colors ${
                    mode === "login" ? "bg-brand-charcoal text-white" : "text-brand-charcoal/70"
                  }`}
                >
                  Sign in
                </button>
                <button
                  type="button"
                  data-testid="auth-tab-register"
                  onClick={() => { setMode("register"); setError(""); }}
                  className={`py-2 text-[11.5px] font-bold uppercase tracking-[0.14em] rounded-lg transition-colors ${
                    mode === "register" ? "bg-brand-charcoal text-white" : "text-brand-charcoal/70"
                  }`}
                >
                  Create account
                </button>
              </div>

              <form onSubmit={onSubmit} className="mt-6 space-y-4" data-testid="auth-form" noValidate>
                {mode === "register" && (
                  <>
                    <div className="grid grid-cols-2 gap-2 rounded-xl bg-brand-cream/40 p-1">
                      <button
                        type="button"
                        data-testid="auth-role-private"
                        onClick={() => setRole("private")}
                        className={`py-2 text-[11px] font-bold uppercase tracking-[0.12em] rounded-lg transition-colors ${
                          role === "private" ? "bg-brand-gold text-brand-charcoal" : "text-brand-charcoal/60"
                        }`}
                      >
                        Private
                      </button>
                      <button
                        type="button"
                        data-testid="auth-role-agency"
                        onClick={() => setRole("agency")}
                        className={`py-2 text-[11px] font-bold uppercase tracking-[0.12em] rounded-lg transition-colors ${
                          role === "agency" ? "bg-brand-teal text-white" : "text-brand-charcoal/60"
                        }`}
                      >
                        Agency
                      </button>
                    </div>
                    <Field label="Full Name *">
                      <Input data-testid="auth-name" value={form.name} onChange={update("name")} className="h-11 rounded-xl bg-brand-cream/40 border-black/10" />
                    </Field>
                    {role === "agency" && (
                      <Field label="Organization">
                        <Input data-testid="auth-org" value={form.organization} onChange={update("organization")} placeholder="e.g. PS 123 Queens" className="h-11 rounded-xl bg-brand-cream/40 border-black/10" />
                      </Field>
                    )}
                  </>
                )}
                <Field label="Email *">
                  <Input data-testid="auth-email" type="email" autoComplete="email" value={form.email} onChange={update("email")} className="h-11 rounded-xl bg-brand-cream/40 border-black/10" />
                </Field>
                <Field label={mode === "login" ? "Password *" : "Password (min 6 chars) *"}>
                  <Input data-testid="auth-password" type="password" autoComplete={mode === "login" ? "current-password" : "new-password"} value={form.password} onChange={update("password")} className="h-11 rounded-xl bg-brand-cream/40 border-black/10" />
                </Field>

                {mode === "login" && (
                  <div className="flex justify-end -mt-1">
                    <Link
                      to="/forgot-password"
                      data-testid="auth-forgot-password-link"
                      className="text-[12px] font-semibold text-brand-teal hover:text-brand-charcoal transition-colors"
                    >
                      Forgot password?
                    </Link>
                  </div>
                )}

                {error && (
                  <p data-testid="auth-error" role="alert" className="text-[12.5px] text-red-600">
                    {error}
                  </p>
                )}

                <button
                  type="submit"
                  data-testid="auth-submit-btn"
                  disabled={submitting}
                  className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-brand-charcoal text-white px-5 py-3.5 text-[12px] font-bold uppercase tracking-[0.14em] hover:bg-brand-teal transition-colors disabled:opacity-60"
                >
                  <Lock className="h-3.5 w-3.5" />
                  {submitting ? "Working…" : mode === "login" ? "Sign in" : "Create account"}
                  <ArrowRight className="h-4 w-4" />
                </button>
              </form>
            </div>
            )}

            <p className="mt-5 text-center text-[11px] text-brand-charcoal/55">
              {pendingApproval
                ? "We'll be in touch shortly."
                : "By continuing, you agree to our service & catering terms."}
            </p>
          </div>
        </section>
      </main>
      <Footer />
      <BackToTop />
    </div>
  );
};

const Field = ({ label, children }) => (
  <div className="flex flex-col gap-1.5">
    <Label className="text-[11px] font-bold uppercase tracking-[0.14em] text-brand-charcoal/65">{label}</Label>
    {children}
  </div>
);

export default AuthPage;
